<template>
  <div class="bg-white q-pa-md full-height">
    <h3 class="q-subheading q-ma-none">{{ $t('expense_data_heading') }}</h3>
    <supplier-select
      :value="local.supplier"
      @input="val => update('supplier', val)"
      :disable="isCommitted"
    />

    <div class="row gutter-sm">
      <q-field
        class="col"
        :error="errors.has('invoiceDate')"
        :error-label="errors.first('invoiceDate')"
      >
        <q-datetime
          name="invoiceDate"
          :value="local.invoiceDate"
          type="date"
          :float-label="$t('invoice_date')"
          :data-vv-as="$t('invoice_date')"
          :format="$t('date_format')"
          v-validate.disable="'required'"
          clearable
          @input="val => update('invoiceDate', val)"
          :disable="isCommitted"
        />
      </q-field>

      <q-field
        class="col"
        :error="errors.has('deliveryDate')"
        :error-label="errors.first('deliveryDate')"
      >
        <q-datetime
          name="deliveryDate"
          :value="local.deliveryDate"
          type="date"
          :float-label="$t('expense_delivery_date')"
          :data-vv-as="$t('expense_delivery_date')"
          :format="$t('date_format')"
          clearable
          @input="val => update('deliveryDate', val)"
          :disable="isCommitted"
        />
      </q-field>
    </div>

    <q-field
      class="col"
      :error="errors.has('accountingPeriod')"
      :error-label="errors.first('accountingPeriod')"
    >
      <q-datetime
        name="accountingPeriod"
        :value="local.accountingPeriod"
        type="date"
        :float-label="$t('accounting_period')"
        :data-vv-as="$t('accounting_period')"
        format="MMMM"
        v-validate.disable="'required'"
        clearable
        @input="val => update('accountingPeriod', val)"
        :disable="isCommitted"
      />
    </q-field>

    <q-field
      :error="errors.has('invoiceNumber')"
      :error-label="errors.first('invoiceNumber')"
    >
      <q-input
        name="invoiceNumber"
        :value="local.invoiceNumber"
        :float-label="$t('invoice_number')"
        :data-vv-as="$t('invoice_number')"
        v-validate.disable="'required'"
        @input="val => update('invoiceNumber', val)"
        :disable="isCommitted"
      />
    </q-field>

    <div class="row">
      <q-field
        class="col-8"
        :error="errors.has('totalNet')"
        :error-label="errors.first('totalNet')"
      >
        <money-input
          name="totalNet"
          type="number"
          :value="local.totalNet"
          :float-label="$t('sum_net')"
          :data-vv-as="$t('sum_net')"
          v-validate.disable="'required|numeric'"
          @input="val => update('totalNet', val)"
          :disable="isCommitted"
        />
      </q-field>

      <currency-input
        :value="local.currency"
        class="col-4"
        :label="$t('currency')"
        floating-label
        disable
      />
    </div>

    <div class="row items-center">
      <q-icon name="info" color="faded"/>

      <span class="q-ml-sm q-body-1 text-faded">{{ $t('expense_convert_currency') }}</span>
    </div>

    <div class="row">
      <q-field
        class="col-8"
        :error="errors.has('salesTaxAmount')"
        :error-label="errors.first('salesTaxAmount')"
      >
        <money-input
          name="salesTaxAmount"
          type="number"
          :value="local.salesTaxAmount"
          :float-label="$t('sales_tax_amount')"
          :data-vv-as="$t('sales_tax_amount')"
          v-validate.disable="'required|numeric'"
          @input="val => update('salesTaxAmount', val)"
          :disable="isCommitted"
        />
      </q-field>

      <q-field
        class="col-4"
        :error="errors.has('salesTaxRate')"
        :error-label="errors.first('salesTaxRate')"
      >
        <sales-tax-input
          :value="local.salesTaxRate"
          :float-label="$t('sales_tax_rate')"
          use-default
          required
          @input="val => update('salesTaxRate', val)"
          :disable="isCommitted"
        />
      </q-field>
    </div>

    <h3 class="q-subheading q-mb-none">{{ $t('payment_terms_heading') }}</h3>

    <div class="row gutter-sm">
      <q-field
        :error="errors.has('cashDiscount')"
        :error-label="errors.first('cashDiscount')"
        class="col-4"
      >
        <q-input
          name="cashDiscount"
          type="number"
          suffix="%"
          :value="local.cashDiscount"
          :float-label="$t('cash_discount')"
          :data-vv-as="$t('cash_discount')"
          @input="val => update('cashDiscount', val)"
          :disable="isCommitted"
        />
      </q-field>

      <q-field
        :error="errors.has('cashDiscountLimit')"
        :error-label="errors.first('cashDiscountLimit')"
        class="col-8"
      >
        <q-datetime
          name="cashDiscountLimit"
          :value="local.cashDiscountLimit"
          type="date"
          :float-label="$t('cash_discount_payment_until')"
          :data-vv-as="$t('cash_discount_payment_until')"
          :format="$t('date_format')"
          clearable
          @input="val => update('cashDiscountLimit', val)"
          :disable="isCommitted"
        />
      </q-field>
    </div>

    <q-field
      :error="errors.has('dueDate')"
      :error-label="errors.first('dueDate')"
    >
      <q-datetime
        name="dueDate"
        :value="local.dueDate"
        type="date"
        :float-label="$t('due_to_without_cash_discount')"
        :data-vv-as="$t('due_to_without_cash_discount')"
        :format="$t('date_format')"
        @input="val => update('dueDate', val)"
        :disable="isCommitted"
      />
    </q-field>

    <q-field
      :error="errors.has('paidAt')"
      :error-label="errors.first('paidAt')"
    >
      <q-datetime
        name="paidAt"
        :value="local.paidAt"
        type="date"
        :float-label="$t('expense_paid_at')"
        :data-vv-as="$t('expense_paid_at')"
        :format="$t('date_format')"
        @input="val => update('paidAt', val)"
      />
    </q-field>

    <h3 class="q-subheading q-mb-none">{{ $tc('project', 1) }}</h3>

    <project-select
      :value="local.projectId"
      :float-label="$tc('project', 1)"
      :data-vv-as="$tc('project', 1)"
      @input="val => update('projectId', val)"
      clearable
    />
  </div>
</template>

<script>
  import CurrencyInput from 'components/Common/Inputs/CurrencyInput.vue'
  import MoneyInput from 'components/Common/Inputs/MoneyInput.vue'
  import ProjectSelect from 'components/Common/Inputs/ProjectSelect.vue'
  import SalesTaxInput from 'components/Common/Inputs/SalesTaxInput.vue'
  import SupplierSelect from 'components/Expenses/SupplierSelect.vue'
  import { calculateSalesTax } from 'src/utils/calculateSalesTax'
  import { isNil } from 'lodash'

  export default {
    inject: ['$validator'],
    components: {
      CurrencyInput,
      SupplierSelect,
      MoneyInput,
      SalesTaxInput,
      ProjectSelect
    },
    props: {
      value: {
        required: true,
        validator: prop => typeof prop === 'object' || prop === null
      }
    },
    computed: {
      local () {
        return this.value
          ? this.value
          : {
            supplier: null,
            invoiceDate: null,
            deliveryDate: null,
            accountingPeriod: null,
            invoiceNumber: null,
            totalNet: null,
            currency: 'EUR',
            salesTaxAmount: null,
            salesTaxRate: null,
            cashDiscount: null,
            cashDiscountLimit: null,
            dueDate: null,
            paidAt: null,
            committedAt: null,
            projectId: null
          }
      },
      invoiceDate () {
        return this.local.invoiceDate
      },
      totalNet () {
        return this.local.totalNet
      },
      salesTaxRate () {
        return this.local.salesTaxRate
      },
      isCommitted () {
        return this.local.committedAt !== null && this.local.committedAt !== undefined
      }
    },
    methods: {
      update (key, value) {
        this.$emit('input', { ...this.local, [key]: value })
      },
      updateAccountingPeriod () {
        if (!isNil(this.local.accountingPeriod)) {
          return
        }

        this.update('accountingPeriod', this.invoiceDate)
      },
      updateSalesTaxAmount () {
        if (isNil(this.salesTaxRate) || isNil(this.totalNet)) {
          return
        }

        this.update('salesTaxAmount', calculateSalesTax(this.totalNet, this.salesTaxRate))
      }
    },
    watch: {
      invoiceDate (val) {
        this.updateAccountingPeriod()
      },
      totalNet (val) {
        this.updateSalesTaxAmount()
      },
      salesTaxRate (val) {
        this.updateSalesTaxAmount()
      }
    }
  }
</script>
